import { useEffect, useState } from "react";

import { faviconFor, hostOf } from "./favicon.ts";

/** Clearbit logo for a domain (no key; hides on error). Fallback under Brandfetch. */
function clearbitLogo(domain: string | undefined): string {
  const host = hostOf(domain);
  return host ? `https://logo.clearbit.com/${host}` : "";
}

/** A palette of soft brand-ish backgrounds for the lettered fallback badge. */
const BADGE_BG = ["#e8f0fe", "#e6f4ea", "#fef7e0", "#fce8e6", "#f3e8fd", "#e0f7fa"];
const BADGE_FG = ["#1a73e8", "#1e8e3e", "#b8860b", "#ea4335", "#8430ce", "#00838f"];

function hashIndex(s: string, n: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % n;
}

/**
 * Company/competitor logo. Tries, in order: the given `logoUrl` (a ready-to-use
 * Brandfetch logo resolved server-side), then Clearbit and a favicon derived from
 * `domain`, then a colored lettered badge — so a real logo shows wherever a company
 * name appears, degrading gracefully on each load error.
 */
export function CompanyLogo({
  name,
  logoUrl,
  domain,
  size = 40,
  rounded = 10,
}: {
  name: string;
  logoUrl?: string;
  /** Company domain/website — used to derive Clearbit/favicon fallbacks. */
  domain?: string;
  size?: number;
  rounded?: number;
}) {
  // Ordered source chain; advance on each load error until we run out → badge.
  const chain = [logoUrl, clearbitLogo(domain), faviconFor(domain)].filter(Boolean) as string[];
  const [idx, setIdx] = useState(0);
  // Restart the chain when the inputs change (e.g. a profile loads and supplies a
  // real logoUrl after we'd already fallen through to a favicon/badge).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on the sources
  useEffect(() => setIdx(0), [logoUrl, domain]);
  const src = chain[idx];
  const letter = (name?.trim()?.[0] ?? "?").toUpperCase();
  const i = hashIndex(name || "?", BADGE_BG.length);

  if (src) {
    return (
      <img
        // Re-mount on src change so a previously-errored <img> retries the next source.
        key={src}
        src={src}
        alt={`${name} logo`}
        width={size}
        height={size}
        onError={() => setIdx((n) => n + 1)}
        style={{
          width: size,
          height: size,
          borderRadius: rounded,
          objectFit: "contain",
          background: "#fff",
          border: "1px solid var(--border-soft)",
          flex: "0 0 auto",
        }}
      />
    );
  }
  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: rounded,
        background: BADGE_BG[i],
        color: BADGE_FG[i],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: size * 0.44,
        flex: "0 0 auto",
      }}
    >
      {letter}
    </div>
  );
}
